import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon
} from '@chakra-ui/react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BloodType } from '@/modules/medicalRecord/domain/models/BloodType'
import { Gender } from '@/modules/medicalRecord/domain/models/PatientProfileMedicalRecord'
import { ResponsibleMedicalRecord } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecord'
import { ResponsibleMedicalRecordReview } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordReview'
import { ResponsibleMedicalRecordView } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordView'
import { ReviewResponsibleMedicalRecordForm } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'
import { CustomSelect } from '@/ui/components/CustomSelect'

import { MedicalRecordReviewType } from '../../../../constants/MedicalRecordReviewType'
import { ReviewDataCheckboxSingleValue } from '../components/ReviewDataCheckbox'
import { useReviewFormRepeatedMultipleValues } from '../components/ReviewFormRepeatedValue'
import ReviewStepWrapper from '../components/ReviewStepWrapper'

export function PatientDataReviewStep({
  data,
  mode
}:
  | {
      data: ResponsibleMedicalRecordView
      mode: typeof MedicalRecordReviewType.enum.VIEW
    }
  | {
      data: ResponsibleMedicalRecordReview
      mode: typeof MedicalRecordReviewType.enum.REVIEW
    }) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()

  useReviewFormRepeatedMultipleValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    values: [
      {
        formProp: 'height',
        responsibleValue: data.sendResponsible.height,
        serverValue: data.registerPatient.height
      },
      {
        formProp: 'weight',
        responsibleValue: data.sendResponsible.weight,
        serverValue: data.registerPatient.weight
      },
      {
        formProp: 'patientBirthDay',
        responsibleValue: data.sendResponsible.patientBirthDay,
        serverValue: data.registerPatient.patientBirthDay
      },
      {
        formProp: 'bloodType',
        responsibleValue: data.sendResponsible.bloodType,
        serverValue: data.registerPatient.bloodType
      },
      {
        formProp: 'patientGender',
        responsibleValue: data.sendResponsible.patientGender,
        serverValue: data.registerPatient.patientGender
      }
    ]
  })

  return (
    <Flex justifyContent={'space-between'} gap={'6'} overflowX={'clip'}>
      <PatientDataReviewPart
        sourceData={data.sendResponsible}
        comparisonData={
          mode === 'REVIEW' ? data.registerPatient : form.getValues()
        }
        mode={mode}
      />
      <PatientDataReviewPart
        sourceData={data.registerPatient}
        comparisonData={
          mode === 'REVIEW' ? data.sendResponsible : form.getValues()
        }
        mode={mode}
      />
    </Flex>
  )
}

function PatientDataReviewPart({
  sourceData,
  comparisonData,
  mode
}: {
  sourceData: ResponsibleMedicalRecord
  comparisonData: ReviewResponsibleMedicalRecordForm | ResponsibleMedicalRecord
  mode: MedicalRecordReviewType
}) {
  const { t } = useTranslation()

  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()
  const [height, weight, patientBirthDay, bloodType, patientGender] =
    form.watch([
      'height',
      'weight',
      'patientBirthDay',
      'bloodType',
      'patientGender'
    ])

  return (
    <ReviewStepWrapper>
      <FormControl
        isInvalid={
          !!sourceData.patientBirthDay &&
          !!form.formState.errors.patientBirthDay?.message
        }
      >
        <FormLabel>Data de nascimento</FormLabel>
        <Input
          type={'date'}
          value={sourceData.patientBirthDay ?? ''}
          isDisabled
          border={'solid 1px'}
          borderColor={
            sourceData.patientBirthDay ? 'custom.gray.400' : 'custom.gray.600'
          }
        />
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.patientBirthDay}
          serverValue={comparisonData.patientBirthDay}
          formValue={patientBirthDay}
          set={(v) => form.setValue('patientBirthDay', v)}
          reset={() => form.setValue('patientBirthDay', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.patientBirthDay?.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={
          !!sourceData.patientGender &&
          !!form.formState.errors.patientGender?.message
        }
      >
        <FormLabel>Gênero</FormLabel>
        <CustomSelect
          options={Gender.options.map((gender) => {
            return {
              value: gender,
              label: t(
                `general.common.gender.${
                  gender.toLowerCase() as Lowercase<typeof gender>
                }`
              )
            }
          })}
          value={sourceData.patientGender ?? undefined}
          disabled
        />
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.patientGender}
          serverValue={comparisonData.patientGender}
          formValue={patientGender}
          set={(v) => form.setValue('patientGender', v)}
          reset={() => form.setValue('patientGender', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.patientGender?.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={
          !!sourceData.bloodType && !!form.formState.errors.bloodType?.message
        }
      >
        <FormLabel>Grupo sanguíneo</FormLabel>
        <CustomSelect
          options={BloodType.options.map((bloodType) => {
            return {
              value: bloodType,
              label: t(`operational.remedy.models.bloodType.${bloodType}`)
            }
          })}
          maxHeight="350px"
          value={sourceData.bloodType ?? undefined}
          disabled
        />
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.bloodType}
          serverValue={comparisonData.bloodType}
          formValue={bloodType}
          set={(v) => form.setValue('bloodType', v)}
          reset={() => form.setValue('bloodType', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.bloodType?.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={
          !!sourceData.weight && !!form.formState.errors.weight?.message
        }
      >
        <FormLabel>Peso</FormLabel>
        <InputGroup>
          <Input
            type={'number'}
            value={sourceData.weight ?? ''}
            isDisabled
            border={'solid 1px'}
            borderColor={
              sourceData.weight ? 'custom.gray.400' : 'custom.gray.600'
            }
          />
          <InputRightAddon>kg</InputRightAddon>
        </InputGroup>
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.weight}
          serverValue={comparisonData.weight}
          formValue={weight}
          set={(v) => form.setValue('weight', v)}
          reset={() => form.setValue('weight', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.weight?.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={
          !!sourceData.height && !!form.formState.errors.height?.message
        }
      >
        <FormLabel>Altura</FormLabel>
        <InputGroup>
          <Input
            type={'number'}
            value={sourceData.height ?? ''}
            isDisabled
            border={'solid 1px'}
            borderColor={
              sourceData.height ? 'custom.gray.400' : 'custom.gray.600'
            }
          />
          <InputRightAddon>cm</InputRightAddon>
        </InputGroup>
        <ReviewDataCheckboxSingleValue
          disabled={mode === MedicalRecordReviewType.enum.VIEW}
          checkboxProps={{ mt: '4' }}
          currentValue={sourceData.height}
          serverValue={comparisonData.height}
          formValue={height}
          set={(v) => form.setValue('height', v)}
          reset={() => form.setValue('height', undefined)}
        />
        <FormErrorMessage>
          {form.formState.errors.height?.message}
        </FormErrorMessage>
      </FormControl>
    </ReviewStepWrapper>
  )
}
